/**
 * Schedules — what goes out, to whom, and when.
 *
 * The screen is built around one rule: **a real dispatch should never be one click
 * away from a list row.** Sending is irreversible — email leaves, borrower data lands
 * in a mailbox, and 7-day links are minted — so the blessed path is Dry run → review
 * the resolved file, recipients and exact columns → Send from inside that panel.
 *
 * "Run now" exists because sometimes a client is on the phone. It is deliberately
 * heavier: it sits behind the row's ⋯ overflow, it always confirms, and when this
 * session has no *matching* dry run for the schedule it also demands the client id be
 * typed out. Editing a schedule invalidates its recorded dry run, because the thing
 * that was reviewed is no longer the thing that would be sent.
 *
 * A stored schedule can also be invalid without anyone touching it — a detailed shape
 * on a month-to-date window is refused by the dispatcher (CONTRACT §3) — so each row
 * is re-validated on render and carries that verdict in the STATE column, which is
 * column one on every table in this console.
 *
 * The row is one line. The single two-line cell this table is allowed is FIRES: the
 * next fire time in IST over the cron read back in English. Everything else is one
 * fact per cell, with the machine form on the title — the identity columns used to
 * stack a name over a slug that wrapped at its hyphens, and one cell set the height of
 * the whole list.
 *
 * LAST RUN is not a column here. Every fixed width plus a usable minimum for the one
 * width-less column has to fit 1,000px — a 1280px laptop once the sidebar and the
 * content padding are taken off — and nine columns do not. A table that does not fit
 * that budget has too many columns rather than a width problem, and last run is the one
 * whose content is stated in full on another screen: /runs, one click away in the
 * toolbar and filterable by client. It stays on the FIRES cell's title.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import Badge from '@/components/Badge';
import Button from '@/components/Button';
import ConfirmDialog from '@/components/ConfirmDialog';
import DryRunPanel from '@/components/DryRunPanel';
import EmptyState from '@/components/EmptyState';
import { Select, Switch } from '@/components/Field';
import { PageHeader } from '@/components/Layout';
import Pagination, { useCursorPagination } from '@/components/Pagination';
import ScheduleEditor from '@/components/ScheduleEditor';
import TestMailDialog from '@/components/TestMailDialog';
import { describeCron } from '@/lib/cron';
import Table, { type Column } from '@/components/Table';
import { useToast } from '@/components/Toast';
import { windowError } from '@/components/WindowPicker';
import { api, errorMessage } from '@/lib/api';
import {
  describeWindow,
  istDateRange,
  istDateTime,
  pluralise,
  resolveWindow,
  timeAgo,
} from '@/lib/format';
import { reportShapeInfo } from '@/lib/types';
import type { Client, DryRunResult, Page, Schedule } from '@/lib/types';

/**
 * Everything about a schedule that changes what would be sent. A dry run is only
 * evidence for the configuration it ran against; change any of this and the operator
 * is back to an unreviewed dispatch.
 */
function scheduleSignature(schedule: Schedule): string {
  return JSON.stringify([
    schedule.report_shape,
    schedule.window,
    [...schedule.include_opt_in].sort(),
    schedule.flow,
    schedule.campaign_pattern,
    schedule.format ?? 'csv',
  ]);
}

interface DryRunRecord {
  at: number;
  signature: string;
  stale: boolean;
  blocked: boolean;
  rowsOut: number;
  recipients: number;
}

interface MenuItem {
  key: string;
  label: ReactNode;
  glyph: string;
  onSelect: () => void;
  title?: string;
  /** Renders the label in the danger hue — for the one item that destroys something. */
  danger?: boolean;
  /** Renders the label in the warn hue — for the one item that sends for real. */
  warn?: boolean;
}

/**
 * The row's overflow. Five buttons per row put forty controls on an eight-row screen,
 * wrapped the actions cell onto two lines, and drew an irreversible dispatch exactly
 * like Edit. One teal Dry run stays on the row; everything else lives in here.
 *
 * Positioned `fixed` from the trigger's own rect rather than absolutely inside the
 * cell: the table scroller clips its overflow in both axes, so an absolutely
 * positioned menu would be cut off by the edge of the table.
 */
function RowActionsMenu({ label, items }: { label: string; items: MenuItem[] }) {
  const [open, setOpen] = useState(false);
  const [at, setAt] = useState<{ top?: number; bottom?: number; right: number } | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const close = (): void => setOpen(false);
    const onKey = (event: KeyboardEvent): void => {
      if (event.key !== 'Escape') return;
      setOpen(false);
      triggerRef.current?.focus();
    };
    const onPointerDown = (event: MouseEvent): void => {
      const target = event.target as Node;
      if (menuRef.current?.contains(target) || triggerRef.current?.contains(target)) return;
      setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onPointerDown);
    window.addEventListener('resize', close);
    // Capture phase: the table body is its own scroller, and a menu pinned to the
    // viewport must not hang in mid-air when the rows move under it.
    window.addEventListener('scroll', close, true);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onPointerDown);
      window.removeEventListener('resize', close);
      window.removeEventListener('scroll', close, true);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    // preventScroll: a focus that scrolls would fire the scroll listener above and shut
    // the menu the instant it opened.
    menuRef.current?.querySelector('button')?.focus({ preventScroll: true });
  }, [open]);

  const toggle = (): void => {
    const rect = triggerRef.current?.getBoundingClientRect();
    if (rect) {
      // Opens upward when the row is near the foot of the list, so the last schedule on
      // a page is not the one whose actions fall off the bottom of the window.
      const height = items.length * 30 + 8;
      const right = Math.max(8, window.innerWidth - rect.right);
      setAt(
        rect.bottom + 4 + height > window.innerHeight
          ? { bottom: Math.max(8, window.innerHeight - rect.top + 4), right }
          : { top: rect.bottom + 4, right },
      );
    }
    setOpen((current) => !current);
  };

  return (
    <>
      <Button
        ref={triggerRef}
        size="sm"
        variant="ghost"
        iconOnly
        icon="⋯"
        title={label}
        aria-label={label}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={toggle}
      />
      {open && at ? (
        <div
          ref={menuRef}
          role="menu"
          aria-label={label}
          // A menu is expected to move on the arrow keys, so it does; Tab still works
          // because every item is a real button.
          onKeyDown={(event) => {
            if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
            event.preventDefault();
            const buttons = [...(menuRef.current?.querySelectorAll('button') ?? [])];
            const here = buttons.indexOf(document.activeElement as HTMLButtonElement);
            const step = event.key === 'ArrowDown' ? 1 : -1;
            const next = (here + step + buttons.length) % buttons.length;
            buttons[next]?.focus({ preventScroll: true });
          }}
          style={{
            position: 'fixed',
            top: at.top,
            bottom: at.bottom,
            right: at.right,
            zIndex: 90,
            minWidth: '210px',
            display: 'flex',
            flexDirection: 'column',
            gap: '1px',
            padding: 'var(--s-1)',
            background: 'var(--surface)',
            border: '1px solid var(--line)',
            borderRadius: 'var(--r-2)',
            boxShadow: 'var(--shadow-2)',
          }}
        >
          {items.map((item) => (
            <Button
              key={item.key}
              role="menuitem"
              size="sm"
              variant="ghost"
              block
              title={item.title}
              style={{
                justifyContent: 'flex-start',
                color: item.danger
                  ? 'var(--danger)'
                  : item.warn
                    ? 'var(--warn)'
                    : undefined,
              }}
              onClick={() => {
                setOpen(false);
                item.onSelect();
              }}
            >
              <span className="mono" aria-hidden="true" style={{ marginRight: 'var(--s-2)' }}>
                {item.glyph}
              </span>
              {item.label}
            </Button>
          ))}
        </div>
      ) : null}
    </>
  );
}

export default function Schedules() {
  const navigate = useNavigate();
  const toast = useToast();
  const pager = useCursorPagination();
  const [params, setParams] = useSearchParams();

  const clientId = params.get('client_id') ?? '';
  const enabledParam = params.get('enabled') ?? '';
  const filtered = Boolean(clientId || enabledParam);

  const [clients, setClients] = useState<Client[]>([]);
  const [page, setPage] = useState<Page<Schedule>>({ items: [], cursor: null });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [busyId, setBusyId] = useState<string | null>(null);

  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState<Schedule | null>(null);
  const [dryRunFor, setDryRunFor] = useState<Schedule | null>(null);
  const [runNowFor, setRunNowFor] = useState<Schedule | null>(null);
  const [testMailFor, setTestMailFor] = useState<Schedule | null>(null);
  const [deleteFor, setDeleteFor] = useState<Schedule | null>(null);

  /** Dry runs recorded in THIS session, keyed by schedule id. Not persisted. */
  const [dryRuns, setDryRuns] = useState<Record<string, DryRunRecord>>({});

  const shownQuery = useRef<string | null>(null);
  const queryKey = `${clientId}|${enabledParam}|${pager.cursor ?? ''}|${pager.pageSize}`;

  useEffect(() => {
    let cancelled = false;
    if (shownQuery.current !== queryKey) setLoading(true);

    api.schedules
      .list({
        cursor: pager.cursor,
        limit: pager.pageSize,
        client_id: clientId || undefined,
        enabled: enabledParam === '' ? undefined : enabledParam === 'true',
      })
      .then((result) => {
        if (cancelled) return;
        setPage(result);
        setError(null);
        shownQuery.current = queryKey;
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(errorMessage(err));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [queryKey, reloadKey, clientId, enabledParam, pager.cursor, pager.pageSize]);

  // Re-read on `clientsKey` as well as on mount: a flow refresh writes observed flows onto
  // the client row, and the editor's picker renders from this list — without this the names
  // it just found would not appear until the page was reloaded.
  const [clientsKey, setClientsKey] = useState(0);
  useEffect(() => {
    let cancelled = false;
    api.clients
      .listAll()
      .then((all) => {
        if (!cancelled) setClients(all);
      })
      .catch(() => {
        if (!cancelled) setClients([]);
      });
    return () => {
      cancelled = true;
    };
  }, [clientsKey]);

  const clientFor = useCallback(
    (id: string) => clients.find((entry) => entry.client_id === id) ?? null,
    [clients],
  );

  /**
   * One line, the human form, with the machine form on the title — never a second line.
   * `company_name` is the same string as `name` on most rows, and a client slug wraps at
   * its hyphens, so this cell used to render six lines and set the height of the table.
   */
  const clientCell = useCallback(
    (schedule: Schedule): ReactNode => {
      const client = clientFor(schedule.client_id);
      const title = [client?.name, client?.company_name, schedule.client_id]
        .filter(Boolean)
        .join(' · ');
      return (
        // A block box, not a span: `overflow` does not apply to a non-replaced inline
        // element, so `.truncate` on a span spills into the next column instead of
        // clipping — which under fixed layout is what an operator actually sees.
        <div className="truncate" title={title || schedule.client_id}>
          {client?.name ?? schedule.client_id}
        </div>
      );
    },
    [clientFor],
  );

  const setFilter = (key: 'client_id' | 'enabled', value: string): void => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next, { replace: true });
    pager.reset();
  };

  const clearFilters = (): void => {
    setParams(new URLSearchParams(), { replace: true });
    pager.reset();
  };

  /** A dry run counts only while it still matches the schedule it was run against. */
  const validDryRun = useCallback(
    (schedule: Schedule): DryRunRecord | null => {
      const record = dryRuns[schedule.schedule_id];
      if (!record) return null;
      if (record.signature !== scheduleSignature(schedule)) return null;
      if (record.stale || record.blocked) return null;
      return record;
    },
    [dryRuns],
  );

  const recordDryRun = useCallback(
    (schedule: Schedule, result: DryRunResult | null) => {
      setDryRuns((current) => {
        if (!result) {
          const { [schedule.schedule_id]: _dropped, ...rest } = current;
          return rest;
        }
        return {
          ...current,
          [schedule.schedule_id]: {
            at: Date.now(),
            signature: scheduleSignature(schedule),
            stale: Boolean(result.stale),
            blocked: Boolean(result.guardrail_error) || (result.missing_source_keys?.length ?? 0) > 0,
            rowsOut: result.rows_out,
            recipients: result.recipients?.length ?? 0,
          },
        };
      });
    },
    [],
  );

  const toggleEnabled = async (schedule: Schedule, next: boolean): Promise<void> => {
    setBusyId(schedule.schedule_id);
    // Optimistic: the switch is the state, so it must move under the finger.
    setPage((current) => ({
      ...current,
      items: current.items.map((entry) =>
        entry.schedule_id === schedule.schedule_id ? { ...entry, enabled: next } : entry,
      ),
    }));
    try {
      const updated = await api.schedules.patch(schedule.schedule_id, { enabled: next });
      setPage((current) => ({
        ...current,
        items: current.items.map((entry) =>
          entry.schedule_id === schedule.schedule_id ? updated : entry,
        ),
      }));
      toast.success(next ? 'Schedule enabled' : 'Schedule disabled', {
        description: next
          ? describeCron(schedule.cron) ?? `Fires on ${schedule.cron} IST.`
          : 'Nothing will be dispatched until it is enabled again.',
      });
    } catch (err) {
      setPage((current) => ({
        ...current,
        items: current.items.map((entry) =>
          entry.schedule_id === schedule.schedule_id ? { ...entry, enabled: !next } : entry,
        ),
      }));
      toast.error(errorMessage(err));
    } finally {
      setBusyId(null);
    }
  };

  const runNow = async (): Promise<void> => {
    if (!runNowFor) return;
    const schedule = runNowFor;
    setBusyId(schedule.schedule_id);
    try {
      const queued = await api.schedules.runNow(schedule.schedule_id);
      toast.success('Dispatch queued', {
        description: `Run ${queued.run_id} is ${queued.status}.`,
        action: { label: 'Open run', onClick: () => navigate(`/runs/${queued.run_id}`) },
      });
      setRunNowFor(null);
      setReloadKey((key) => key + 1);
    } catch (err) {
      toast.error(errorMessage(err));
      throw err;
    } finally {
      setBusyId(null);
    }
  };

  const remove = async (): Promise<void> => {
    if (!deleteFor) return;
    const schedule = deleteFor;
    setBusyId(schedule.schedule_id);
    try {
      await api.schedules.remove(schedule.schedule_id);
      toast.success('Schedule deleted', {
        description: `${schedule.name ?? schedule.schedule_id} will not fire again.`,
      });
      setDeleteFor(null);
      setReloadKey((key) => key + 1);
    } catch (err) {
      toast.error(errorMessage(err));
      throw err;
    } finally {
      setBusyId(null);
    }
  };

  /**
   * Column rank decides position (CONSOLE-UX §1.5): state first, then the subject, then
   * the facts that prove what would go out, then context. The widths are a budget —
   * everything that is not `.col-optional` fits 1,000px, which is a 1280px laptop with
   * the sidebar and the content padding taken off. SCHEDULE is the one column declared
   * without a width, so it absorbs whatever slack a wider screen gives back.
   */
  const columns: Column<Schedule>[] = [
    {
      key: 'state',
      header: 'State',
      width: '148px',
      nowrap: true,
      render: (schedule) => {
        // Re-validated on render: a stored schedule goes bad on its own when the clock
        // moves the window, and the dispatcher would refuse it at 07:30 without anyone
        // touching it.
        const problem = windowError(schedule.window, schedule.report_shape);
        if (problem) {
          return (
            <Badge tone="danger" glyph="▲" title={problem}>
              Would be refused
            </Badge>
          );
        }
        return schedule.enabled ? (
          <Badge tone="ok" glyph="✓" title="Enabled — it fires on the cron beside it">
            On
          </Badge>
        ) : (
          <Badge tone="muted" glyph="○" title="Parked — nothing is dispatched">
            Off
          </Badge>
        );
      },
    },
    {
      key: 'name',
      header: 'Schedule',
      render: (schedule) => (
        <div
          className="strong truncate"
          title={
            schedule.name
              ? `${schedule.name} · ${schedule.schedule_id}`
              : schedule.schedule_id
          }
        >
          {schedule.name ?? schedule.schedule_id}
        </div>
      ),
    },
    {
      key: 'client',
      header: 'Client',
      width: '180px',
      render: (schedule) => clientCell(schedule),
    },
    {
      key: 'fires',
      header: 'Fires next (IST)',
      width: '152px',
      nowrap: true,
      headerTitle: 'The next time this schedule triggers, resolved in IST',
      // The one two-line cell this table is allowed. The second line is a different
      // fact from the first — when it next fires, over the rule that decides it —
      // rather than the same fact reformatted.
      render: (schedule) => {
        const reading = describeCron(schedule.cron) ?? 'custom expression, IST';
        const lastRun = schedule.last_run_at
          ? `last run ${istDateTime(schedule.last_run_at)} (${timeAgo(schedule.last_run_at)})`
          : 'never run';
        return (
          <div className="stack-tight" title={`${reading} · ${schedule.cron} · ${lastRun}`}>
            {!schedule.enabled ? (
              <span className="dim">— parked</span>
            ) : schedule.next_run_at ? (
              <span className="num">{istDateTime(schedule.next_run_at)}</span>
            ) : (
              <span className="dim">— not scheduled</span>
            )}
            <span className="xs dim truncate">{reading}</span>
          </div>
        );
      },
    },
    {
      key: 'window',
      header: 'Window (IST)',
      width: '152px',
      nowrap: true,
      headerTitle: 'What each run reads, resolved in IST at fire time',
      render: (schedule) => {
        const resolved = resolveWindow(schedule.window);
        const label = describeWindow(schedule.window);
        return (
          <div
            className="truncate"
            // `custom` already IS the range, so the resolved line is never repeated
            // under it; for the relative types the title is where it resolves right now.
            title={`${label} · ${istDateRange(resolved.from, resolved.to)} IST · ${pluralise(resolved.days, 'day')}`}
          >
            {label} <span className="dim">· {pluralise(resolved.days, 'day')}</span>
          </div>
        );
      },
    },
    {
      key: 'shape',
      header: 'Shape',
      width: '104px',
      nowrap: true,
      optional: true,
      render: (schedule) => {
        // A report shape is a classification, not a state: it cannot be wrong and it
        // never changes on its own. Three of eight rows used to be permanently amber
        // for a perfectly legal configuration, which is how amber stops meaning
        // "look at this".
        const shape = reportShapeInfo(schedule.report_shape);
        return (
          <Badge variant="plain" glyph={shape.glyph} title={shape.hint}>
            {schedule.report_shape}
          </Badge>
        );
      },
    },
    {
      key: 'opt_in',
      header: 'Opt-in',
      width: '120px',
      nowrap: true,
      headerTitle: 'Call-content columns this schedule releases (CONTRACT §6)',
      render: (schedule) =>
        // The one place warn is earned on this table: these are the columns derived
        // from what was said on the call, leaving the building.
        schedule.include_opt_in.length === 0 ? (
          <span className="dim">—</span>
        ) : (
          <Badge tone="warn" glyph="◇" title={schedule.include_opt_in.join(', ')}>
            {schedule.include_opt_in.length} call content
          </Badge>
        ),
    },
    {
      key: 'actions',
      header: '',
      width: '148px',
      nowrap: true,
      align: 'right',
      render: (schedule) => {
        const reviewed = validDryRun(schedule);
        return (
          <div className="cluster-tight" style={{ justifyContent: 'flex-end', flexWrap: 'nowrap' }}>
            {/* The control, beside the other controls. Its STATE is the badge in
                column one, which is what survives a greyscale screenshot; the switch
                itself carries no visible label, so the row stays one line. */}
            <Switch
              checked={schedule.enabled}
              disabled={busyId === schedule.schedule_id}
              ariaLabel={
                schedule.enabled
                  ? `Disable ${schedule.name ?? schedule.schedule_id}`
                  : `Enable ${schedule.name ?? schedule.schedule_id}`
              }
              label={null}
              onChange={(next) => void toggleEnabled(schedule, next)}
            />
            {/* Teal unconditionally. The accent used to move down the column depending
                on which rows this session happened to have dry-run, so the one colour
                that means "you can act on this" changed meaning row by row. */}
            <Button
              size="sm"
              variant="primary"
              onClick={() => setDryRunFor(schedule)}
              title={
                reviewed
                  ? `Reviewed ${timeAgo(reviewed.at)} in this session. Resolves the source file, recipients and exact columns again, without sending.`
                  : 'Resolve the source file, recipients and exact columns without sending'
              }
            >
              Dry run
            </Button>
            <RowActionsMenu
              label={`More actions for ${schedule.name ?? schedule.schedule_id}`}
              items={[
                {
                  key: 'run',
                  glyph: '▲',
                  label: 'Run now — sends for real',
                  warn: true,
                  title: reviewed
                    ? `Reviewed ${timeAgo(reviewed.at)} — sends for real`
                    : 'No matching dry run in this session — you will be asked to type the client id',
                  onSelect: () => setRunNowFor(schedule),
                },
                {
                  key: 'test',
                  glyph: '✉',
                  label: 'Send a test',
                  title:
                    "Send this schedule's real report to one internal address, before a client ever sees it",
                  onSelect: () => setTestMailFor(schedule),
                },
                {
                  key: 'edit',
                  glyph: '✎',
                  label: 'Edit',
                  onSelect: () => {
                    setEditing(schedule);
                    setEditorOpen(true);
                  },
                },
                {
                  key: 'delete',
                  glyph: '✕',
                  label: 'Delete',
                  danger: true,
                  title: 'Delete this schedule',
                  onSelect: () => setDeleteFor(schedule),
                },
              ]}
            />
          </div>
        );
      },
    },
  ];

  const runNowReviewed = runNowFor ? validDryRun(runNowFor) : null;
  const runNowClient = runNowFor ? clientFor(runNowFor.client_id) : null;
  const dryRunClient = dryRunFor ? clientFor(dryRunFor.client_id) : null;

  const handleDryRunResult = useCallback(
    (result: DryRunResult | null) => {
      if (dryRunFor) recordDryRun(dryRunFor, result);
    },
    [dryRunFor, recordDryRun],
  );

  return (
    <div className="stack">
      <PageHeader
        title="Schedules"
        subtitle="Dry-run before you send. Every cron below is evaluated in IST."
        actions={
          <Button
            variant="primary"
            onClick={() => {
              setEditing(null);
              setEditorOpen(true);
            }}
          >
            New schedule
          </Button>
        }
      />

      <div className="toolbar">
        <label className="cluster-tight">
          <span className="small dim">Client</span>
          <Select
            size="sm"
            value={clientId}
            aria-label="Filter by client"
            onChange={(event) => setFilter('client_id', event.target.value)}
          >
            <option value="">All clients</option>
            {clients.map((client) => (
              <option key={client.client_id} value={client.client_id}>
                {client.name}
              </option>
            ))}
          </Select>
        </label>

        <label className="cluster-tight">
          <span className="small dim">State</span>
          <Select
            size="sm"
            value={enabledParam}
            aria-label="Filter by enabled state"
            onChange={(event) => setFilter('enabled', event.target.value)}
          >
            <option value="">Any state</option>
            <option value="true">Enabled</option>
            <option value="false">Disabled</option>
          </Select>
        </label>

        {filtered ? (
          <Button size="sm" variant="ghost" onClick={clearFilters}>
            Clear filters
          </Button>
        ) : null}

        <span className="grow" />
        <Button size="sm" variant="ghost" onClick={() => navigate('/runs')}>
          Runs →
        </Button>
      </div>

      <Table
        columns={columns}
        rows={page.items}
        rowKey={(schedule) => schedule.schedule_id}
        loading={loading}
        dense
        stickyHeader
        rowTone={(schedule) =>
          windowError(schedule.window, schedule.report_shape)
            ? 'danger'
            : schedule.enabled
              ? 'default'
              : 'muted'
        }
        empty={
          error ? (
            <EmptyState
              tone="danger"
              title="Could not load schedules"
              description={error}
              action={
                <Button variant="primary" onClick={() => setReloadKey((key) => key + 1)}>
                  Retry
                </Button>
              }
            />
          ) : filtered ? (
            <EmptyState
              tone="filtered"
              title="No schedules match those filters"
              action={
                <Button variant="primary" onClick={clearFilters}>
                  Clear filters
                </Button>
              }
            />
          ) : (
            <EmptyState
              title="No schedules yet"
              description="A schedule decides what is built, for whom, and when it fires."
              action={
                <Button
                  variant="primary"
                  onClick={() => {
                    setEditing(null);
                    setEditorOpen(true);
                  }}
                >
                  New schedule
                </Button>
              }
            />
          )
        }
        footer={
          <Pagination
            page={pager.page}
            pageSize={pager.pageSize}
            count={page.items.length}
            hasNext={Boolean(page.cursor)}
            hasPrev={pager.hasPrev}
            onNext={() => pager.next(page.cursor)}
            onPrev={pager.prev}
            onPageSize={pager.setPageSize}
            loading={loading}
            label="schedules"
          />
        }
      />

      <ScheduleEditor
        open={editorOpen}
        schedule={editing}
        clients={clients}
        defaultClientId={clientId || undefined}
        onClientsChanged={() => setClientsKey((key) => key + 1)}
        onClose={() => {
          setEditorOpen(false);
          setEditing(null);
        }}
        onSaved={(saved) => {
          // The configuration changed, so any dry run recorded against it is history.
          setDryRuns((current) => {
            const { [saved.schedule_id]: _dropped, ...rest } = current;
            return rest;
          });
          setReloadKey((key) => key + 1);
        }}
      />

      <TestMailDialog
        open={testMailFor !== null}
        schedule={testMailFor}
        client={testMailFor ? clientFor(testMailFor.client_id) : null}
        onClose={() => setTestMailFor(null)}
      />

      <DryRunPanel
        open={dryRunFor !== null}
        schedule={dryRunFor}
        client={dryRunClient}
        onClose={() => setDryRunFor(null)}
        onResult={handleDryRunResult}
        onDispatched={(runId) => {
          setReloadKey((key) => key + 1);
          navigate(`/runs/${runId}`);
        }}
      />

      <ConfirmDialog
        open={runNowFor !== null}
        tone={runNowReviewed ? 'warn' : 'danger'}
        title="Dispatch this schedule now?"
        confirmLabel="Send for real"
        cancelLabel="Cancel"
        consequence={`Email leaves immediately and cannot be recalled. Recipients get a link that works for ${runNowClient?.link_ttl_days ?? 7} days.`}
        confirmPhrase={runNowReviewed ? undefined : runNowFor?.client_id}
        onCancel={() => setRunNowFor(null)}
        onConfirm={runNow}
      >
        <div className="stack-tight">
          <div>
            {runNowFor?.name ?? runNowFor?.schedule_id} ·{' '}
            {runNowFor ? describeWindow(runNowFor.window) : ''} ·{' '}
            {runNowFor?.report_shape ?? 'borrower'} shape
          </div>
          {runNowReviewed ? (
            <div className="small dim">
              Dry run {timeAgo(runNowReviewed.at)}: {pluralise(runNowReviewed.rowsOut, 'row')} to{' '}
              {pluralise(runNowReviewed.recipients, 'recipient')}.
            </div>
          ) : (
            <div className="callout tone-warn">
              <span className="callout-glyph" aria-hidden="true">
                ▲
              </span>
              <span>
                No matching dry run in this session, so nobody has seen which file, which
                recipients or which columns this would send. Close this and dry-run it instead
                unless you already know.
              </span>
            </div>
          )}
        </div>
      </ConfirmDialog>

      <ConfirmDialog
        open={deleteFor !== null}
        tone="danger"
        title="Delete this schedule?"
        confirmLabel="Delete schedule"
        consequence="It stops firing immediately and cannot be restored. Runs it already produced, and their links, are unaffected."
        confirmPhrase={deleteFor?.schedule_id}
        onCancel={() => setDeleteFor(null)}
        onConfirm={remove}
      >
        <div>
          {deleteFor?.name ?? deleteFor?.schedule_id} for{' '}
          {deleteFor ? (clientFor(deleteFor.client_id)?.name ?? deleteFor.client_id) : ''}
        </div>
      </ConfirmDialog>
    </div>
  );
}
